"use client";

import * as React from "react";
import { useChromeStrings } from "@/components/shared/article/chrome-strings";
import { toast } from "sonner";

import {
  initialComments,
  type Comment,
  type CommentReply,
} from "@/fixtures/shared/article/comment-fixtures";

/**
 * Headless state machine for the article comment drawers — QA sweep, 2026-08.
 *
 * There are five near-duplicate comment drawers in the codebase (shared/article,
 * ej-education, ej-tech, ej-monthly, article-detail-new). Every behaviour bug QA
 * filed had to be fixed in each of them; this hook holds the behaviour once and
 * leaves the skins to the components.
 *
 * Defects this fixes, all reported against /ej-education/article-detail:
 *   - "1) 編輯訊息後，時間戳不應該被編輯日期時間覆蓋掉吧？ 2) 而且如果有編輯過，
 *     最好在後面顯示編輯過的時間" — editing replaced `time` with "剛剛 (已編輯)",
 *     destroying the original post time. Now `time` is immutable and `editedAt`
 *     is separate.
 *   - "如果不小心關閉了這個回覆框，不要清空之前輸入的留言內容" and the same for
 *     editing — drafts are retained per target so reopening restores the text.
 *   - "我自己的帳號發表的留言 缺少刪除留言選項？" — `deleteComment`.
 *   - "當選擇「其他」時，應該顯示一個新的輸入原因欄位…並且在允許使用者提交之前，
 *     驗證該原因欄位不得為空白" — `canSubmitReport`.
 *   - "提交檢舉之後並無成功或者失敗嘅提示" — the submit handlers raise a toast.
 *     `<Toaster />` is already mounted in app/[locale]/(public)/layout.tsx.
 */

export type CommentMode =
  | { kind: "list" }
  | { kind: "reply"; commentId: string }
  | { kind: "edit"; commentId: string }
  | { kind: "report"; commentId: string }
  /** Staged deletion — the row asks, the overlay confirms. */
  | { kind: "delete"; commentId: string }
  | { kind: "linkedin" };

/** The free-text reason is required when this option is chosen. */
export const REPORT_OTHER = "其他";

export const REPORT_REASONS = [
  "垃圾訊息或廣告",
  "仇恨言論或騷擾",
  "不實資訊",
  "暴力或危險內容",
  REPORT_OTHER,
] as const;

/**
 * The same five reasons, keyed for translation.
 *
 * `REPORT_REASONS` stays as it is because its strings are the VALUES a report
 * is submitted with, and four drawers already render them directly. A drawer
 * that serves an English edition maps over this instead: same values, labels
 * from `messages/*.json`.
 */
export const REPORT_REASON_KEYS: ReadonlyArray<{
  value: (typeof REPORT_REASONS)[number];
  key: string;
}> = [
  { value: "垃圾訊息或廣告", key: "reasonSpam" },
  { value: "仇恨言論或騷擾", key: "reasonHate" },
  { value: "不實資訊", key: "reasonFalse" },
  { value: "暴力或危險內容", key: "reasonViolence" },
  { value: REPORT_OTHER, key: "reasonOther" },
];

export function canSubmitReport(
  reason: string | null,
  otherText: string
): boolean {
  if (!reason) return false;
  if (reason === REPORT_OTHER) return otherText.trim().length > 0;
  return true;
}

/** How long after posting a comment stays editable and deletable. */
export const EDIT_WINDOW_MINUTES = 6 * 60;

/**
 * Age of a comment in minutes, read off its display timestamp.
 *
 * The fixtures carry relative Chinese strings ("剛剛", "2小時前", "1天前")
 * rather than epoch times, and there is no backend to ask, so the window in
 * `canModifyComment` is derived from the same string the row renders. Anything
 * unparseable is treated as old, which fails closed: an unrecognised stamp
 * hides the edit/delete controls instead of offering an action that may be
 * past its window. Swap this for a real `createdAt` when the API lands.
 */
export function commentAgeMinutes(time: string): number {
  if (/剛剛|刚刚/.test(time)) return 0;
  const n = Number(time.match(/\d+/)?.[0]);
  if (!Number.isFinite(n)) return Number.POSITIVE_INFINITY;
  if (/分鐘|分钟/.test(time)) return n;
  if (/小時|小时/.test(time)) return n * 60;
  if (/天/.test(time)) return n * 60 * 24;
  if (/週|周|星期/.test(time)) return n * 60 * 24 * 7;
  if (/月/.test(time)) return n * 60 * 24 * 30;
  if (/年/.test(time)) return n * 60 * 24 * 365;
  return Number.POSITIVE_INFINITY;
}

/**
 * Whether the reader may still edit or delete their own comment — QA: "the
 * edit and delete button will disable/disappear after 6 hours of the time the
 * comment was made".
 */
export function canModifyComment(comment: Comment | CommentReply): boolean {
  const c = comment as Comment;
  // `isOwn` now lives on `CommentReply`, so this answers for a reply as well as
  // a top-level comment — it used to be false for every reply.
  if (!comment.isOwn || c.isDeleted) return false;
  return commentAgeMinutes(comment.time) < EDIT_WINDOW_MINUTES;
}

export type CommentSort = "newest" | "oldest" | "replies" | "likes";

export const COMMENT_SORTS: ReadonlyArray<{
  value: CommentSort;
  label: string;
}> = [
  { value: "newest", label: "最新" },
  { value: "oldest", label: "最舊" },
  { value: "replies", label: "最多回覆" },
  { value: "likes", label: "最多讚好" },
];

export function useCommentThread(seed: Comment[] = initialComments) {
  /**
   * The thread's toasts are LOCALISED.
   *
   * They were five hardcoded Chinese strings, which is right for four of the
   * five products that mount this hook and wrong for EJ Insight, an English
   * edition — QA: "toast language should be in english for ejinsight". The
   * wording for the Chinese locales is unchanged; it now lives in
   * `messages/*.json` under `article.comments`.
   */
  const t = useChromeStrings("comments");
  const [comments, setComments] = React.useState<Comment[]>(seed);
  const [sort, setSort] = React.useState<CommentSort>("newest");
  const [mode, setMode] = React.useState<CommentMode>({ kind: "list" });
  const [composer, setComposer] = React.useState("");

  /**
   * Draft text keyed by `${mode}:${commentId}`. Kept OUTSIDE `mode` so closing
   * an overlay does not discard it — the previous implementation carried an
   * optional `text` on the mode object and never populated it on cancel, so
   * every accidental close lost the draft.
   */
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});

  const draftKey = (kind: string, id: string) => `${kind}:${id}`;

  const getDraft = React.useCallback(
    (kind: "reply" | "edit", commentId: string, fallback = "") =>
      drafts[draftKey(kind, commentId)] ?? fallback,
    [drafts]
  );

  const setDraft = React.useCallback(
    (kind: "reply" | "edit", commentId: string, text: string) =>
      setDrafts((d) => ({ ...d, [draftKey(kind, commentId)]: text })),
    []
  );

  const clearDraft = React.useCallback(
    (kind: "reply" | "edit", commentId: string) =>
      setDrafts((d) => {
        const next = { ...d };
        delete next[draftKey(kind, commentId)];
        return next;
      }),
    []
  );

  /** Close the current overlay, KEEPING any draft. */
  const closeOverlay = React.useCallback(() => setMode({ kind: "list" }), []);

  const toggleLike = React.useCallback((id: string) => {
    setComments((prev) =>
      prev.map((c) => {
        if (c.id === id) {
          return { ...c, liked: !c.liked, likes: c.liked ? c.likes - 1 : c.likes + 1 };
        }
        if (c.replies) {
          return {
            ...c,
            replies: c.replies.map((r) =>
              r.id === id
                ? { ...r, liked: !r.liked, likes: r.liked ? r.likes - 1 : r.likes + 1 }
                : r
            ),
          };
        }
        return c;
      })
    );
  }, []);

  const addComment = React.useCallback((body: string) => {
    const text = body.trim();
    if (!text) return false;
    setComments((prev) => [
      {
        id: `new-${prev.length}-${text.length}`,
        author: "你",
        time: "剛剛",
        body: text,
        likes: 0,
        avatar: "/images/article-comments/avatar.png",
        isOwn: true,
      },
      ...prev,
    ]);
    setComposer("");
    toast.success(t("toastPosted"));
    return true;
  }, []);

  const addReply = React.useCallback(
    (commentId: string, body: string) => {
      const text = body.trim();
      if (!text) return false;
      const reply: CommentReply = {
        id: `r-${commentId}-${text.length}`,
        author: "你",
        time: "剛剛",
        body: text,
        likes: 0,
        avatar: "/images/article-comments/avatar.png",
        // Without this the reader's own reply failed `canModifyComment` and
        // rendered with no 修改 / 刪除 controls at all.
        isOwn: true,
      };
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId ? { ...c, replies: [...(c.replies ?? []), reply] } : c
        )
      );
      clearDraft("reply", commentId);
      closeOverlay();
      toast.success(t("toastReplied"));
      return true;
    },
    [clearDraft, closeOverlay]
  );

  /** Edits the body and stamps `editedAt`. `time` is never touched. */
  const editComment = React.useCallback(
    (commentId: string, body: string) => {
      const text = body.trim();
      if (!text) return false;
      const stamp = (c: Comment | CommentReply) => ({
        ...c,
        body: text,
        editedAt: "剛剛",
      });
      setComments((prev) =>
        prev.map((c) => {
          if (c.id === commentId) return stamp(c) as Comment;
          if (c.replies) {
            return {
              ...c,
              replies: c.replies.map((r) => (r.id === commentId ? stamp(r) : r)),
            };
          }
          return c;
        })
      );
      clearDraft("edit", commentId);
      closeOverlay();
      toast.success(t("toastEdited"));
      return true;
    },
    [clearDraft, closeOverlay]
  );

  /**
   * Removes the comment. NOT to be called straight from the row's 刪除 button:
   * deletion is irreversible and had no confirmation step, so a mis-tap on a
   * 40px control destroyed the comment outright. QA: "delete comment: should
   * have a confirmation modal before deleting". The drawer stages
   * `{ kind: "delete" }` and calls this only from the modal's 刪除.
   */
  const deleteComment = React.useCallback((commentId: string) => {
    setComments((prev) =>
      prev
        .filter((c) => c.id !== commentId)
        .map((c) =>
          c.replies
            ? { ...c, replies: c.replies.filter((r) => r.id !== commentId) }
            : c
        )
    );
    toast.success(t("toastDeleted"));
  }, []);

  const submitReport = React.useCallback(
    (reason: string | null, otherText: string) => {
      if (!canSubmitReport(reason, otherText)) {
        toast.error(t("toastReportNeedsReason"));
        return false;
      }
      closeOverlay();
      // TODO(api): POST the report once a moderation endpoint exists.
      toast.success(t("toastReported"));
      return true;
    },
    [closeOverlay]
  );

  /**
   * The 排序 control used to be a static label with no menu — QA asked for real
   * options. Sorting happens here rather than in the drawer so all five skins
   * order identically. Replies keep their own chronological order; only the
   * top-level thread is re-ordered.
   */
  const sortedComments = React.useMemo(() => {
    const list = [...comments];
    if (sort === "likes") {
      list.sort((a, b) => b.likes - a.likes);
    } else if (sort === "replies") {
      list.sort((a, b) => (b.replies?.length ?? 0) - (a.replies?.length ?? 0));
    } else {
      const dir = sort === "newest" ? 1 : -1;
      list.sort(
        (a, b) => dir * (commentAgeMinutes(a.time) - commentAgeMinutes(b.time))
      );
    }
    return list;
  }, [comments, sort]);

  return {
    comments: sortedComments,
    sort,
    setSort,
    mode,
    setMode,
    composer,
    setComposer,
    getDraft,
    setDraft,
    /** Discard a draft outright — for an explicit 取消, as opposed to the
     *  accidental close `closeOverlay` is written to survive. */
    clearDraft,
    closeOverlay,
    toggleLike,
    addComment,
    addReply,
    editComment,
    deleteComment,
    submitReport,
    findById: React.useCallback(
      (id: string) => findCommentById(comments, id),
      [comments]
    ),
  };
}

export function findCommentById(
  comments: Comment[],
  id: string
): Comment | CommentReply | undefined {
  for (const c of comments) {
    if (c.id === id) return c;
    if (c.replies) for (const r of c.replies) if (r.id === id) return r;
  }
  return undefined;
}
